<script setup lang="ts">
import { onMounted, ref } from 'vue';
import ConnxTransition from '@/components/Atoms/ConnxTransition.vue';
import ConnxConfirmationModal from '@/components/Molecules/ConnxConfirmationModal.vue';
import ConnxPill from '@/components/Atoms/ConnxPill.vue';
import ConnxIcon from '@/components/Atoms/ConnxIcon.vue';
import ConnxAlert from '@/components/Molecules/ConnxAlert.vue';
import ConnxButton from '@/components/Atoms/ConnxButton.vue';
import ConnxTfaSettingsDeviceList from './ConnxTFASettingsDeviceList.vue';
import useTFAuthenticationSettingsStore from '@/stores/tf-authentication-settings';
import useApplicationStore from '@/stores/application';
import useAuthenticationDevices from '@/stores/tf-authentication-devices';

interface Props {
  verifyLink : string;
}

const props = defineProps<Props>();

const authenticationStore = useTFAuthenticationSettingsStore();
const authenticationDevices = useAuthenticationDevices();
const applicationStore = useApplicationStore();

const loaded = ref(false);
const resetConfirmationOpen = ref(false);

const toggleResetConfirmationOpen = () => {
  resetConfirmationOpen.value = !resetConfirmationOpen.value;
};

const resetTFADevices = async () => {
  resetConfirmationOpen.value = false;
  applicationStore.setLoading(true);
  await authenticationDevices.removeAllTrustedDevices();
  window.location.replace(props.verifyLink);
};

onMounted(async () => {
  await authenticationStore.fetchGracePeriod();
  await authenticationStore.fetchVerifyTrustedDevice();
  loaded.value = true;
});
</script>

<template>
  <div class="tw-mt-4 tw-px-4 sm:tw-px-0 tw-max-w-7xl">
    <div>
      <h3 class="tw-text-xl tw-mb-4">
        Two Factor Authentication Settings
      </h3>
      <p>
        Two Factor Authentication adds an extra layer of protection to your account.
      </p>
      <p>A verification code generated from your authenticator app will be required when you log in.</p>
      <connx-transition>
        <div v-show="loaded">
          <div
            class="
            tw-flex
            tw-flex-wrap
            tw-items-center
            tw-justify-start
            tw-max-w-2xl
            tw-gap-x-10
          "
          >
            <p class="md:tw-hidden tw-mt-4">
              Current Status:
            </p>
            <p class="tw-hidden md:tw-block tw-mt-4">
              Two Factor Authentication:
            </p>

            <connx-pill
              class="tw-block tw-grow tw-mt-4"
              :class="{
                'tw-px-2': !authenticationStore.trustedDevice.isVerifiedEmployee,
              }"
              :type="authenticationStore.trustedDevice.isVerifiedEmployee ? 'success' : 'warning'"
            >
              <span v-if="authenticationStore.trustedDevice.isVerifiedEmployee">Verified</span>
              <span v-else>Pending Verification</span>
            </connx-pill>

            <connx-alert
              v-if="!authenticationStore.trustedDevice.isVerifiedEmployee"
              type="warning"
              class="tw-mt-4 tw-w-full sm:tw-order-last"
            >
              You have
              {{ authenticationStore.gracePeriod.daysToSkip2faSetup }}
              days remaining to configure Two Factor Authentication
            </connx-alert>

            <connx-button
              v-if="authenticationStore.trustedDevice.isVerifiedEmployee"
              icon="fas fa-undo"
              icon-position="before"
              class="tw-mx-auto tw-mt-4"
              @click="toggleResetConfirmationOpen()"
            >
              Reset
            </connx-button>
            <connx-button
              v-else
              button-style="primary"
              :link="verifyLink"
              icon="far fa-shield-check"
              icon-position="before"
              class="tw-mx-auto tw-mt-4"
            >
              Verify Now
            </connx-button>

            <connx-confirmation-modal :open="resetConfirmationOpen" @close="toggleResetConfirmationOpen()"
            confirmation-button-text="Yes, reset"
            @confirmed="resetTFADevices()"
            >
              <p>WARNING: ALL Two Factor Authentication data will be cleared,
                you will be redirected to set up Two Factor Authentication.</p>
            </connx-confirmation-modal>
          </div>
          <div v-if="authenticationStore.trustedDevice.isVerifiedEmployee">
            <Suspense>
              <template #default>
                <connx-tfa-settings-device-list />
              </template>
            </Suspense>
          </div>
        </div>
      </connx-transition>
    </div>
  </div>
</template>
